<template>
  <BasicTable
    ref="tableRef"
    :tableMode="tableMode"
    v-bind="bindTableEvents"
    @register="registerTable"
  >
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>

    <template v-if="tableProps.api && tableProps.formConfig?.['showPivot']" #form-advanceAfter>
      <MButton color="purple" type="primary" @click="showPivotModal">{{ t('g.26050002') }}</MButton>
      <PivotModal :fetchApi="tableProps.api" v-bind="tableAction" @register="registerPivot" />
    </template>

    <template
      v-if="tableProps.tableTabs?.length || tableProps.headerBtns?.length || $slots.tableTitleBtns"
      #tableTitle
    >
      <TableTitle
        v-if="tableProps.title"
        :helpMessage="tableProps.titleHelpMessage"
        :title="tableProps.title"
      />
      <Radio.Group
        v-if="tableProps.tableTabs?.length"
        v-model:value="tableMode"
        buttonStyle="solid"
        optionType="button"
        :options="tableProps.tableTabs"
      />
      <HeaderBtns
        v-if="tableProps.headerBtns?.length"
        :schema="tableProps.headerBtns"
        :record="headerData"
        :tableAction="tableAction"
        :actionHandler="actionHandler"
      />
      <slot name="tableTitleBtns"></slot>
    </template>

    <template v-if="$slots.toolbarBtns" #toolbar>
      <slot name="toolbarBtns"></slot>
      <MButton v-if="tableProps.isTreeTable" type="text" size="small" @click="openExpandAll(true)">
        <template #icon>
          <MIcon icon="quanbuzhankai" size="12" class="mr-1" />
        </template>
        全部展开
      </MButton>
      <MButton v-if="tableProps.isTreeTable" type="text" size="small" @click="openExpandAll(false)">
        <template #icon>
          <MIcon icon="shouqi" size="12" class="mr-1" />
        </template>
        全部收起
      </MButton>
    </template>

    <template #tableAfter>
      <BasePageModal
        ref="pageModalRef"
        :getContainer="() => ($refs.tableRef as any)?.wrapRef"
        @register="registerModal"
        @ok="tableAction?.reload"
      >
        <template v-for="item in getModalSlotKeys" #[replaceModalSlotKey(item)]="data">
          <slot :name="item" v-bind="data || {}"></slot>
        </template>
      </BasePageModal>
    </template>
  </BasicTable>
</template>

<script setup lang="ts">
  import { message, Radio } from 'ant-design-vue';
  import { merge, omit, omitBy } from 'lodash-es';
  import { ComponentOptionsMixin, computed, ref, shallowRef, useSlots } from 'vue';
  import { composeBeforeFetch, getRowSpanCount } from '@mts-fe/utils';
  import { MIcon } from '@mts/components/Icon';
  import { useModal } from '@mts/components/Modal';
  import { PivotModal } from '@mts/components/Pivot';
  import { BasicTable, TableTitle, useTable } from '@mts/components/Table';
  import { t } from '@mts/hooks/web/useI18n';
  import { warn } from '@mts/utils';
  import { router } from '@/router';
  import type { ActionHandlerFn, BaseFormSchema, RenderTableColumn, SearchTableProps } from '..';
  import {
    TABLE_EMIT_EVENTS,
    useColumnFetch,
    useDynamicColumns,
    useRowSelectionCellMerge,
    useTableEvents,
  } from '../hooks/useRenderTable';
  import {
    COMPARATOR,
    getFilterInfo,
    getFilterType,
    getSearchType,
    getTableSearch,
    setSearchDefaultValue,
    useActionHandler,
    useMapValueComps,
    usePageModal,
    usePageModalSlot,
    useSchemaActionContext,
    useSchemaPageContext,
  } from '..';
  import BasePageModal from './BasePageModal.vue';
  import HeaderBtns from './HeaderBtns.vue';

  defineOptions({
    name: 'SearchTable',
    extends: BasicTable as ComponentOptionsMixin,
  });

  defineProps<{
    /** 顶部按钮可用数据 */
    headerData?: Recordable;
  }>();
  const emit = defineEmits(['mounted', ...TABLE_EMIT_EVENTS]);
  const bindTableEvents = useTableEvents(emit, (evt: string, ...args: Recordable[]) => {
    if (evt === 'edit-change') {
      const { value, column, record, index } = args[0];
      emit('change', value, column?.dataIndex, record, index, { ...args, tableAction });
    }
  });
  const searchType = shallowRef<Recordable<COMPARATOR>>({});
  const filterType = shallowRef<Recordable<COMPARATOR>>({});
  const tableProps = ref<SearchTableProps>({ columns: [] });
  const tableMode = ref('TABLE');

  const setProps = (
    {
      searchInfo = {},
      enableSearchCache,
      searchType: defSearchType = {},
      filterType: defFilterType = {},
      defSort = {},
      actions = [],
      modals = [],
      afterBeforeFetch,
      ..._tableProps
    }: SearchTableProps & {
      afterBeforeFetch?: (params: Recordable) => Recordable | false;
    },
    partUpdate = false,
  ) => {
    if (partUpdate) {
      actions.length && Object.assign(_tableProps, { actions });
      return tableAction.setProps(_tableProps, true);
    }

    pageModalAction.setModals(modals);

    Object.keys(defSearchType).length &&
      warn('searchType建议直接配在查询字段上 {"comparator":"="}', defSearchType);
    searchType.value = getSearchType(_tableProps.formConfig?.schemas, defSearchType);
    filterType.value = getFilterType(_tableProps.columns, defFilterType);

    setSearchDefaultValue(_tableProps.formConfig?.schemas, searchInfo);

    if (enableSearchCache && router.currentRoute.value.meta.keepSearchInfo) {
      const searchCacheKey = `MTS-SEARCH-INFO-${router.currentRoute.value.path}`;
      _tableProps.searchCacheKey = searchCacheKey;

      if (router.currentRoute.value.query.clearSearch !== '1') {
        const cache = sessionStorage.getItem(searchCacheKey);
        if (cache) {
          const cacheSearch = omit(JSON.parse(cache), ['page', 'size', 'order', 'filterInfo']);
          setSearchDefaultValue(_tableProps.formConfig?.schemas, cacheSearch);
        }
      }
    }

    tableProps.value = merge(_tableProps, {
      immediate: _tableProps.immediate ?? true,
      searchInfo,
      filterInfo: getFilterInfo(_tableProps.columns, tableProps.value.filterInfo),
      defSort: { order: defSort },
      resizeHeightOffset: _tableProps.resizeHeightOffset ?? 8,
      beforeFetch:
        _tableProps.beforeFetch || composeBeforeFetch<any>(beforeFetch, afterBeforeFetch),
      actionHandler,
      actions,
    });
    const bindAfterFetch = tableProps.value.afterFetch;
    if (bindAfterFetch && typeof bindAfterFetch === 'function') {
      tableProps.value.afterFetch = (dataSource, res) => {
        afterFetch(dataSource);
        return bindAfterFetch(dataSource, res);
      };
    }

    console.log('[SearchTable] props', { ...tableProps.value });
    // @ts-ignore
    useColumnFetch(tableProps.value.columns, tableAction);
    // @ts-ignore
    tableAction.setProps(tableProps.value);
    tableMode.value = tableProps.value.tableTabs?.[0]?.value?.toString() || 'TABLE';
    emit('mounted');
  };
  /** 设置表格搜索项 merge */
  const setSearchInfo = (search: Recordable) => {
    const searchInfo = tableAction.getSearchInfo()?.search || {};
    Object.assign(searchInfo, search);
    tableAction.setProps({ searchInfo }, true);
  };
  const reset = async () => {
    tableAction.getForm?.()?.resetFields(true);
    tableAction.clearSelectedRowKeys();
  };

  const afterFetch = (dataSource: Recordable[]) => {
    const { rowKey, defaultExpandAllRows, dynamicColumn, rowSelection } = tableProps.value;

    if (!rowKey && tableAction.getSelectRowKeys().length) {
      tableAction.clearSelectedRowKeys();
      console.warn('[SearchTable] 因为rowKey不存在，已清空已选项');
    }

    expandAllOpen.value && defaultExpandAllRows && setTimeout(tableAction.expandAll, 16);

    const columns = tableAction.getColumns() as RenderTableColumn[];
    if (columns.filter((column) => column.mergeCellBy).length) {
      const mergedColumns = columns.map((column) => {
        if (column.mergeCellBy) {
          // 单元格合并
          const keys = [...column.mergeCellBy.split(','), column.dataIndex!.toString()];
          const bindCustomCell = column.customCell;
          column.customCell = (...args) => ({
            ...bindCustomCell?.(...args),
            rowSpan: getRowSpanCount(dataSource, keys, args[1]),
          });
        }
        return column;
      });
      tableAction.setColumns(mergedColumns, true);
    }

    if (tableMode.value === 'TABLE' && dataSource.length && dynamicColumn?.enabled) {
      const { appendAfter, ...dynamicOption } = dynamicColumn;
      const dynamicColumns = dynamicAction.createDynamicColumnsByData(dataSource, dynamicOption);
      dynamicColumns.length !== dynamicAction.getDynamicColumns().length &&
        dynamicAction.setDynamicColumns(dynamicColumns, appendAfter);
    }

    rowSelection && useRowSelectionCellMerge(rowSelection, dataSource, tableAction);
  };

  const beforeFetch = ({ page, size, order, filterInfo, ...search }) => {
    console.log(`[SearchTable] ${tableProps.value.tableId} fetch`, search);
    const params = {
      modelId: tableProps.value.modelId,
      page,
      size,
      order,
      search: getTableSearch(
        search,
        searchType.value,
        tableProps.value.formConfig?.schemas as BaseFormSchema[],
      ),
      searchType: searchType.value,
      filter: { ...tableProps.value.filterInfo, ...filterInfo },
      filterType: filterType.value,
    };
    tableProps.value.flatParam && Object.assign(params, params.search);
    return params;
  };

  const [registerTable, tableAction] = useTable({
    immediate: false,
    filterFn: (data) => omitBy(data, (i) => i === null),
    beforeFetch,
    afterFetch,
  });
  const dynamicAction = useDynamicColumns(tableAction);

  const expandAllOpen = ref(true);
  const openExpandAll = (open: boolean) => {
    expandAllOpen.value = open;
    open ? tableAction.expandAll() : tableAction.collapseAll();
  };

  const [registerPivot, { openModal: openPivotModal }] = useModal();
  const showPivotModal = () => {
    const pagination = tableAction.getPaginationRef();
    if (typeof pagination !== 'boolean' && pagination.total! > 10000) {
      message.error(t('c.26030313'));
      return;
    }

    const columns = tableAction
      .getProps()
      .columns.filter((i) => !i.flag || i.flag === 'DYNAMIC')
      .map((col) =>
        col.render && useMapValueComps.includes(col.render.type)
          ? { ...col, dataIndex: col.dataIndex + '_label' }
          : col,
      );
    openPivotModal(true, { fetchParams: { ...tableAction.getSearchInfo(), size: 1000 }, columns });
  };

  const slots = useSlots();
  const { replaceModalSlotKey, getModalSlotKeys } = usePageModalSlot(slots);
  const [registerModal, pageModalAction] = usePageModal('SearchTable');
  const pageModalRef = ref();

  const pageContext = useSchemaPageContext();
  const actionContext = useSchemaActionContext();
  const { apiActionHandler, tableActionHandler } = useActionHandler({
    showModal: pageModalAction.showModal,
    pageContext,
    actionContext,
    actionRef: computed(() => tableAction),
    callback: async () => {
      tableAction?.reload();
    },
  });
  const actionHandler: ActionHandlerFn = async (actionEvent, record, index) => {
    const res = await tableActionHandler(actionEvent, record, index);
    return typeof res === 'boolean' && !res ? apiActionHandler(actionEvent, record, index) : void 0;
  };

  defineExpose({
    ...tableAction,
    ...dynamicAction,
    setProps,
    setSearchInfo,
    reset,
    pageModalRef,
    pageModalAction,
    modalsRef: pageModalAction.modalsRef,
  });
</script>
